import { Layout as AntLayout, Button, Dropdown } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

import { HEADER_HEIGHT } from '../../../constants/layout';
import Breadcrumb from '../../ui/Breadcrumb/Breadcrumb';
import type { BreadcrumbItem } from '../../ui/Breadcrumb/Breadcrumb';
import SwitchLanguage from '../../ui/SwitchLanguage/SwitchLanguage';
import { useAuth } from '@xinchuantw/auth-react';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { getProfileColorFromName } from '../../../lib/profileColor';
import { cn } from '../../../lib/utils';
const { Header: AntdHeader } = AntLayout;

interface HeaderProps {
  title: string;
  breadcrumbItems: BreadcrumbItem[];
  /** Left offset in px, in sync with the content's own padding — Layout
   * computes this since it also knows whether the sidebar is overlaying
   * (narrow screens) rather than pushing content aside. */
  sidebarOffset: number;
}

export default function Header({ title, breadcrumbItems, sidebarOffset }: HeaderProps) {
  const { logout, userData } = useAuth();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const crumbs =
    breadcrumbItems.length > 0 ? breadcrumbItems : title.trim() ? [{ label: title }] : [];

  const userMenuItems = [
    {
      key: 'logout',
      label: (
        <div className="flex items-center gap-2 px-2 text-gray-700" onClick={() => logout()}>
          <FontAwesomeIcon icon={faArrowRightFromBracket} />
          {t('sidebar.logout')}
        </div>
      ),
    },
  ];

  return (
    <AntdHeader
      className="
        fixed
        top-0
        right-0
        z-30
        flex
        items-center
        justify-between
        border-b
        border-b-slate-300
        bg-white!
        px-4!
        py-2!
        shadow-sm
        sm:px-5!
        sm:py-3!
      "
      style={{
        height: HEADER_HEIGHT,
        left: sidebarOffset,
      }}
    >
      {/* Left: Breadcrumb or Title */}
      <div className="min-w-0 flex-1 pr-2 sm:pr-3">
        {crumbs.length > 0 ? (
          <Breadcrumb items={crumbs} />
        ) : (
          <h1 className="truncate text-sm font-semibold sm:text-lg"></h1>
        )}
      </div>

      <div className="flex items-center py-3 mx-1 gap-2 sm:mx-4 sm:gap-3">
        {/* Only go compact (short codes like "EN") on phone-sized viewports
            — on desktop/tablet there's always enough room for the full
            label, even with the sidebar expanded. */}
        <SwitchLanguage compact={isMobile} />

        <div className="hidden text-xs text-gray-500 sm:block"> | </div>

        <Dropdown
          menu={{
            items: userMenuItems,
            className: ' h-10',
          }}
          trigger={['click']}
        >
          <Button
            type="text"
            className="flex h-8! items-center! lg:items-end rounded-md! border! border-gray-200! bg-white! px-1.5! text-left! hover:bg-gray-50! sm:h-10! sm:px-2!"
          >
            {/* Avatar */}
            <div
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-lg text-xs font-medium text-gray-700! sm:h-7 sm:w-7',
                getProfileColorFromName(userData?.name || ''),
                'bg-[#FF8904]!' // ! forces it to override
              )}
            >
              {userData?.name?.charAt(0) || 'U'}
            </div>

            {/* Name + Username */}
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-sm font-medium text-gray-700">{userData?.name || 'User'}</span>
            </div>
          </Button>
        </Dropdown>
      </div>
    </AntdHeader>
  );
}
